import { useEffect } from 'react';

import { Button, Group, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { closeAllModals } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';

import { User } from '@interfaces/users.interface';

import { useGetCustomerByIdQuery } from '@api/customer.api';
import { useApproveCustomerMutation } from '@api/users.api';

import MarketCategoriesSelector from './MarketCategoriesSelector';

interface ApproveUserModalProps {
  user: User;
}

export default function ApproveCustomerModal({ user }: ApproveUserModalProps) {
  // ==========================================================================
  // General
  // ==========================================================================

  // ==========================================================================
  // State
  // ==========================================================================

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data: customer } = useGetCustomerByIdQuery(user.id);
  const [approveCustomer, { isLoading }] = useApproveCustomerMutation();

  // ==========================================================================
  // Form
  // ==========================================================================
  const initialValues = {
    marketCategories: [] as string[],
  };

  const form = useForm({
    initialValues,
  });

  useEffect(() => {
    if (customer) {
      form.setFieldValue('marketCategories', customer.allowedCategories);
    }
  }, [customer]);

  // ==========================================================================
  // Handlers
  // ==========================================================================

  const handleApproveUser = async (values: typeof initialValues) => {
    try {
      await approveCustomer({
        id: user.id,
        body: { allowedCategories: values.marketCategories },
      }).unwrap();

      showNotification({
        title: 'Utente approvato',
        message: "L'utente è stato approvato con successo",
      });

      closeAllModals();
    } catch (e) {
      console.error(e);
    }
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <form onSubmit={form.onSubmit(handleApproveUser)}>
      <Stack gap="xs">
        <Group gap="xs">
          <Text fw="bold">Nome: </Text>
          <Text>{user.firstName}</Text>
        </Group>
        <Group gap="xs">
          <Text fw="bold">Cognome: </Text>
          <Text>{user.lastName}</Text>
        </Group>
        <Group gap="xs">
          <Text fw="bold">Email: </Text>
          <Text>{user.email}</Text>
        </Group>
        <Text fw="bold">Mercati di interesse:</Text>
        <MarketCategoriesSelector
          label={undefined}
          {...form.getInputProps('marketCategories')}
        />

        {form.errors.general && (
          <Text c="red" size="sm" mt="xl">
            {form.errors.general}
          </Text>
        )}
        <Group justify="end" mt="xl">
          <Button variant="outline" onClick={() => closeAllModals()}>
            Annulla
          </Button>
          <Button type="submit" loading={isLoading}>
            Approva utente
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
